// Bloc « Location » du tableau de bord : campagnes ouvertes, nouveaux dossiers, visites à venir, messages non lus.
import { Link } from "react-router";
import { date } from "@core/format";
import { useGet } from "@core/hooks";
import { ButtonLink, Card, ErrorBox, Loading } from "@core/ui";
import { CampaignBadge, parisDateTime } from "./shared";

type Summary = {
  open_campaigns: { id: string; title: string; status: string; status_label: string; property_label: string; submitted: number; to_review: number }[];
  new_applications: { id: string; household_label: string; campaign_title: string; submitted_at: string }[];
  upcoming_visits: { slot_id: string; campaign_id: string; campaign_title: string; starts_at: string; capacity: number; booked: number }[];
  unread_messages: number;
};

export function LocationDashboard({ orgId }: { orgId: string }) {
  const q = useGet<Summary>(`/api/v1/orgs/${orgId}/location/summary`);
  const to = (p: string) => `/espace/${orgId}/${p}`;
  return (
    <Card title="Location" actions={<Link to={to("campagnes")} className="small">Toutes les campagnes</Link>}>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <div className="stack">
          {q.data.open_campaigns.length ? (
            <ul className="list-plain location-timeline">
              {q.data.open_campaigns.map((c) => (
                <li key={c.id}>
                  <Link to={to(`campagnes/${c.id}?onglet=candidatures`)}>{c.title}</Link> <CampaignBadge status={c.status} label={c.status_label} />
                  <span className="block muted small">
                    {c.property_label} · {c.submitted} dossier{c.submitted > 1 ? "s" : ""} déposé{c.submitted > 1 ? "s" : ""}
                    {c.to_review ? ` · ${c.to_review} à examiner` : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="stack-sm">
              <p className="muted">Aucune campagne ouverte. Ouvrez une campagne sur un logement pour recevoir des dossiers.</p>
              <ButtonLink small variant="secondaire" to={to("campagnes/nouvelle")}>
                Nouvelle campagne
              </ButtonLink>
            </div>
          )}
          {q.data.new_applications.length ? (
            <div>
              <p className="stat-label">Nouveaux dossiers (14 derniers jours)</p>
              <ul className="list-plain small">
                {q.data.new_applications.map((a) => (
                  <li key={a.id}>
                    <Link to={to(`candidatures/${a.id}`)}>{a.household_label || "Candidature"}</Link> · {a.campaign_title} · {date(a.submitted_at)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {q.data.upcoming_visits.length ? (
            <div>
              <p className="stat-label">Visites des 7 prochains jours (heure de Paris)</p>
              <ul className="list-plain small">
                {q.data.upcoming_visits.map((v) => (
                  <li key={v.slot_id}>
                    <Link to={to(`campagnes/${v.campaign_id}?onglet=visites`)}>{parisDateTime(v.starts_at, true)}</Link> · {v.campaign_title} · {v.booked} / {v.capacity} réservation{v.capacity > 1 ? "s" : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {q.data.unread_messages ? <p className="small">{q.data.unread_messages} message(s) de candidats non lu(s).</p> : null}
        </div>
      ) : null}
    </Card>
  );
}
